// File: frontend/src/components/common/BootSplash.tsx

import React, { useEffect, useState } from "react";
import "./BootSplash.css";

/** After this long without translations, assume something is actually wrong. */
const TIMEOUT_MS = 8000;

/**
 * Suspense fallback shown while the translation bundle is still loading.
 *
 * This renders BEFORE i18n is ready, so every string here has to be
 * hardcoded — t() would return raw keys. The two languages the site supports
 * are picked from the browser locale.
 *
 * Previously this slot held a bare "Loading translations..." div, which is
 * both untranslated and meaningless to a reader; on a failed locale fetch it
 * was also the last thing the user ever saw.
 */
const isTurkish = (): boolean => {
  // This is a Turkish-first site. The splash renders BEFORE i18n initializes,
  // so we can't call t(). Respect an explicit language choice cached by
  // i18next (localStorage "i18nextLng"); otherwise default to Turkish instead
  // of the browser/OS locale, so an English computer still sees "Yükleniyor…".
  try {
    const stored = localStorage.getItem("i18nextLng");
    if (stored) return stored.toLowerCase().startsWith("tr");
  } catch {
    // localStorage may be unavailable (private mode) — fall through to default
  }
  return true;
};

const COPY = {
  tr: {
    loading: "Yükleniyor...",
    slowTitle: "Bağlantı kurulamadı",
    slowBody:
      "İnternet bağlantını kontrol edip tekrar dene. Sorun sende değilse birazdan düzelmiş olur.",
    retry: "Tekrar dene",
  },
  en: {
    loading: "Loading...",
    slowTitle: "Couldn't connect",
    slowBody:
      "Check your internet connection and try again. If the problem isn't on your side, it should clear shortly.",
    retry: "Try again",
  },
};

export const BootSplash: React.FC = () => {
  const [timedOut, setTimedOut] = useState(false);
  const copy = isTurkish() ? COPY.tr : COPY.en;

  useEffect(() => {
    const id = window.setTimeout(() => setTimedOut(true), TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <div className="boot-splash">
      {/* Static SVG asset — safe to reference before the JS bundle/i18n are
          ready, since it's just a plain file under /public. */}
      <img src="/favicon.svg" alt="" className="boot-splash__logo" />
      <div className="boot-splash__title">3 Korner 1 Penaltı</div>

      {timedOut ? (
        <div className="boot-splash__error">
          <div className="boot-splash__error-title">{copy.slowTitle}</div>
          <p className="boot-splash__error-body">{copy.slowBody}</p>
          <button
            type="button"
            className="boot-splash__retry"
            onClick={() => window.location.reload()}
          >
            {copy.retry}
          </button>
        </div>
      ) : (
        <>
          <div className="boot-splash__spinner" aria-hidden="true" />
          <div className="boot-splash__text">{copy.loading}</div>
        </>
      )}
    </div>
  );
};